import { useNextRouter } from '@/data/Content/_NextRouter';
import { Box, Button, CircularProgress, Paper, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export const DPGetRefoundPDF: React.FC = () => {
	const router = useNextRouter();
	const [loading, setLoading] = useState<boolean>(false);
	const [orderFound, setOrderFound] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const [initialLoading, setInitialLoading] = useState<boolean>(true);

	const { orderId, langId, storeId } = router.query;

	const fetchRefundPDF = async () => {
		if (!orderId) {
			setError('No se encontró la orden');
			setInitialLoading(false);
			return;
		}

		setLoading(true);
		setInitialLoading(true);
		try {
			const response = await fetch(
				`/wcs/resources/store/${
					storeId || '1'
				}/devolucion/${orderId}?responseFormat=json&onlyGuide=1&langId=${langId || '-5'}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);

			if (!response.ok) {
				throw new Error(`Error: ${response.status}`);
			}

			const data = await response.json();

			if (data && data.data && data.data.canDownloadGuia && data.data.pdf) {
				setOrderFound(true);
				downloadPDF(data.data.pdf);
			} else {
				throw new Error('PDF no disponible en la respuesta');
			}
		} catch (error) {
			setOrderFound(false);
			setError('No se puede descargar la guía');
		} finally {
			setLoading(false);
			setInitialLoading(false);
		}
	};

	useEffect(() => {
		fetchRefundPDF();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderId, langId, storeId]);

	const downloadPDF = (base64: string) => {
		const byteCharacters = atob(base64);
		const byteNumbers = new Array(byteCharacters.length);
		byteCharacters.split('').forEach((char, i) => {
			byteNumbers[i] = char.charCodeAt(0);
		});
		const byteArray = new Uint8Array(byteNumbers);
		const fileBlob = new Blob([byteArray], { type: 'application/pdf' });
		const link = document.createElement('a');
		link.href = URL.createObjectURL(fileBlob);
		link.download = 'dpdirecto_guia.pdf';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleRestart = () => {
		setOrderFound(false);
		setError('');
		fetchRefundPDF();
	};

	return (
		<Box
			display="flex"
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			height="100vh"
			textAlign="center"
			sx={{ p: 2 }}
		>
			<Paper
				elevation={3}
				sx={{
					p: 4,
					borderRadius: 2,
					maxWidth: 500,
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
				}}
			>
				{initialLoading ? (
					<>
						<Skeleton variant="circular" width={60} height={60} sx={{ mb: 2 }} />
						<Skeleton variant="text" width="80%" height={40} sx={{ mb: 1 }} />
						<Skeleton variant="text" width="60%" height={30} sx={{ mb: 2 }} />
						<Skeleton variant="rectangular" width="100%" height={50} />
					</>
				) : (
					<>
						{!orderId ? (
							<>
								<ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
								<Typography variant="h5" fontWeight={700} textTransform="uppercase" gutterBottom>
									{'No se encontró la orden'}
								</Typography>
							</>
						) : !orderFound ? (
							<>
								<ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
								<Typography variant="h5" fontWeight={700} textTransform="uppercase" gutterBottom>
									{error || 'No se puede descargar la guía'}
								</Typography>
							</>
						) : (
							<>
								<DownloadIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
								<Typography variant="h5" fontWeight={700} textTransform="uppercase" gutterBottom>
									{'Se ha Descargado tu guía'}
								</Typography>
							</>
						)}

						{loading ? <CircularProgress sx={{ my: 2 }} /> : null}

						<Button
							variant="contained"
							color="primary"
							startIcon={<RefreshIcon />}
							onClick={handleRestart}
							disabled={loading}
							sx={{ mt: 3, textTransform: 'uppercase', fontWeight: 600 }}
						>
							{'Intentar nuevamente'}
						</Button>
					</>
				)}
			</Paper>
		</Box>
	);
};
